import React from 'react';
import './GovernanceSafetyChart.css';

/**
 * Hardcoded palette per spec. Kept inline so it survives a CSS-extract step
 * without losing the JS-side references used for inline segment widths and
 * the alert badge icon stroke colour.
 */
const C = {
  safe:       '#639922',
  safeBg:     '#EAF3DE',
  safeText:   '#3B6D11',
  medium:     '#BA7517',
  risk:       '#A32D2D',
  riskBg:     '#FCEBEB',
  riskBorder: '#F09595',
};

const GROUP_ORDER = ['Content safety', 'Prompt injection', 'Response quality'];

const LEVELS = ['low', 'medium', 'high'];

/**
 * Returns the colour token to use for (polarity, level). Encodes SAFETY,
 * not the literal level name — so a "low" bucket on a good-high signal
 * (e.g. "Safe content low = 5%") shows as red because that IS the risky end.
 */
function safetyKindFor(polarity, level) {
  if (level === 'medium') return 'medium';
  if (polarity === 'bad-high') return level === 'low' ? 'safe' : 'risk';
  return level === 'low' ? 'risk' : 'safe';
}

function pctFor(count, total) {
  if (!total) return 0;
  return Math.round((count / total) * 100);
}

/**
 * Compute the (single) worst alert across all signals.
 *
 * Rules (per spec):
 *   - good-high signal with high% < 50  → deviation = 50 - highPct
 *   - bad-high  signal with high% > 10  → deviation = highPct - 10
 *   - bad-high  signal with medium% > 20 → deviation = mediumPct - 20
 *
 * Returns the highest-deviation offender or null when none fire.
 */
function computeWorstAlert(signals) {
  let worst = null;
  Object.keys(signals || {}).forEach((key) => {
    const s = signals[key];
    if (!s) return;
    const total = (s.low || 0) + (s.medium || 0) + (s.high || 0);
    if (!total) return;
    const highPct = pctFor(s.high || 0, total);
    const medPct = pctFor(s.medium || 0, total);

    const candidates = [];
    if (s.polarity === 'good-high' && highPct < 50) {
      candidates.push(50 - highPct);
    }
    if (s.polarity === 'bad-high' && highPct > 10) {
      candidates.push(highPct - 10);
    }
    if (s.polarity === 'bad-high' && medPct > 20) {
      candidates.push(medPct - 20);
    }

    if (candidates.length === 0) return;
    const deviation = Math.max(...candidates);
    if (!worst || deviation > worst.deviation) {
      worst = { key, label: s.label, deviation };
    }
  });
  return worst;
}

function groupSignals(signals) {
  const buckets = new Map();
  GROUP_ORDER.forEach((g) => buckets.set(g, []));
  Object.keys(signals || {}).forEach((key) => {
    const s = signals[key];
    if (!s) return;
    if (!buckets.has(s.group)) buckets.set(s.group, []);
    buckets.get(s.group).push({ key, ...s });
  });
  return Array.from(buckets.entries()).filter(([, items]) => items.length > 0);
}

const Swatch = ({ color }) => (
  <span className="gsc__swatch" style={{ background: color }} aria-hidden="true" />
);

const SignalRow = ({ signal, onClick }) => {
  const total = (signal.low || 0) + (signal.medium || 0) + (signal.high || 0);
  const lowPct = pctFor(signal.low || 0, total);
  const medPct = pctFor(signal.medium || 0, total);
  const highPct = pctFor(signal.high || 0, total);

  const segmentWidth = (count, pct) =>
    count > 0 ? `max(2px, ${pct}%)` : '0%';

  const polarityText =
    signal.polarity === 'bad-high' ? 'high = bad' : 'high = good';

  const cellKinds = LEVELS.map((lvl) => safetyKindFor(signal.polarity, lvl));
  const pcts = { low: lowPct, medium: medPct, high: highPct };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className="gsc__row"
      role="button"
      tabIndex={0}
      data-signal-key={signal.key}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      aria-label={`${signal.label}: low ${lowPct}%, medium ${medPct}%, high ${highPct}%`}
    >
      <div className="gsc__label">
        <span className="gsc__label-name">{signal.label}</span>
        <span className="gsc__label-polarity">{polarityText}</span>
      </div>

      <div
        className="gsc__bar"
        role="img"
        aria-label={`${signal.label} distribution`}
      >
        {LEVELS.map((level, idx) => {
          const count = signal[level] || 0;
          const pct = pcts[level];
          return (
            <span
              key={level}
              className={`gsc__bar-segment gsc__bar-segment--${cellKinds[idx]}`}
              style={{ width: segmentWidth(count, pct) }}
              data-level={level}
              data-pct={pct}
              aria-hidden="true"
            />
          );
        })}
      </div>

      {LEVELS.map((level, idx) => (
        <div
          key={level}
          className={`gsc__count gsc__count--${cellKinds[idx]}`}
          data-level={level}
        >
          {pcts[level]}%
        </div>
      ))}
    </div>
  );
};

/**
 * GovernanceSafetyChart
 *
 * Props (TS-style for reference):
 *
 *   interface GovernanceChartProps {
 *     totalRuns: number;
 *     signals: {
 *       [key: string]: {
 *         label: string;
 *         group: 'Content safety' | 'Prompt injection' | 'Response quality';
 *         polarity: 'bad-high' | 'good-high';
 *         low: number;
 *         medium: number;
 *         high: number;
 *       };
 *     };
 *     onRowClick?: (signalKey: string) => void;
 *   }
 */
const GovernanceSafetyChart = ({ totalRuns = 0, signals = {}, onRowClick }) => {
  const grouped = groupSignals(signals);
  const alert = computeWorstAlert(signals);

  const handleRowClick = (key) => {
    if (typeof onRowClick === 'function') onRowClick(key);
  };

  return (
    <div
      className="gsc"
      role="region"
      aria-label="Governance and safety overview"
    >
      <div className="gsc__header">
        <div className="gsc__title-block">
          <h2 className="gsc__title">Governance &amp; safety</h2>
          <p className="gsc__subtitle">
            {totalRuns} runs · % of runs at each risk level
          </p>
        </div>

        <div className="gsc__header-right">
          {alert && (
            <div className="gsc__alert" role="status">
              <span className="gsc__alert-icon" aria-hidden="true" />
              <span>{alert.label} needs attention</span>
            </div>
          )}
          <div className="gsc__legend" aria-hidden="true">
            <span className="gsc__legend-item">
              <Swatch color={C.safe} /> Low
            </span>
            <span className="gsc__legend-item">
              <Swatch color={C.medium} /> Medium
            </span>
            <span className="gsc__legend-item">
              <Swatch color={C.risk} /> High
            </span>
          </div>
        </div>
      </div>

      <div className="gsc__col-headers" aria-hidden="true">
        <div />
        <div />
        <div className="gsc__col-header-count">Low</div>
        <div className="gsc__col-header-count">Med</div>
        <div className="gsc__col-header-count">High</div>
      </div>

      {grouped.map(([groupName, items]) => (
        <section key={groupName} className="gsc__group">
          <div className="gsc__group-header">{groupName.toUpperCase()}</div>
          {items.map((signal) => (
            <SignalRow
              key={signal.key}
              signal={signal}
              onClick={() => handleRowClick(signal.key)}
            />
          ))}
        </section>
      ))}

    </div>
  );
};

export { computeWorstAlert, safetyKindFor, pctFor, C };
export default GovernanceSafetyChart;
